import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import Separator from "../ui/Separator";
import {
  useBodies,
  useCameras,
  useEqualities,
  useSceneActions,
  useSceneStore,
  useMaterials,
  useSites,
  useTendons,
  useMeshes,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { hasPhysicsWorker } from "../../physics/workerHandle";
import Tooltip from "../ui/Tooltip";
import { Plus } from "lucide-react";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import {
  addDefaultCollisionGeom,
  addDefaultInertia,
  addDefaultSite,
  addDefaultVisualGeom,
} from "../../utils/bodyAddActions";

import { focusNewEntityInInspector } from "../../utils/focusNewEntityInInspector";
import { BodyVisibilityToolbar } from "../../selected-body-visibility";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import RelatedConstraintsSection, {
  computeRelatedConstraintsLists,
} from "./RelatedConstraintsSection";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { unitRgbToHex } from "../../utils/colorUtils";
import { DEFAULT_INSPECTOR_ADD_GEOM_KIND } from "./BodyAddInlineMenu";
import BodyContactExclusionsSection from "./BodyContactExclusionsSection";
import { BodyJointsSection, ChildBodyJointsSection } from "./BodyJointSections";
import BodyTransformSection from "./BodyTransformSection";
import InspectableMenuRow from "./InspectableMenuRow";
import { useMaterialTextureOptions } from "../draggable-panels/material-editor/useMaterialTextureOptions";
import {
  BoxGeomIcon,
  CapsuleGeomIcon,
  CylinderGeomIcon,
  EllipsoidIcon,
  SphereGeomIcon,
  VisualMeshIcon,
} from "../ui/Icons";
import BodyQuickAddSection from "./BodyQuickAddSection";

const EMPTY_LIST = Object.freeze([]);

const resolveGeomMaterial = (geom, materials) => {
  const materialRef = Array.isArray(geom?.materials) ? geom.materials[0] : null;
  if (materialRef && materials?.[materialRef]) return materials[materialRef];
  if (materialRef && materials) {
    const materialByName = Object.values(materials).find((mat) => mat?.name === materialRef);
    if (materialByName) return materialByName;
  }
  return Object.values(materials || {}).find((mat) => mat?.name === "default_mat") || null;
};

const materialColorToCss = (material, fallback = "#ffffff") => {
  const color = Array.isArray(material?.color) ? material.color : null;
  if (!color) return fallback;
  return unitRgbToHex(color, [1, 1, 1]);
};

const getMaterialPreviewLabel = (geom, material) => {
  const materialRef = Array.isArray(geom?.materials) ? geom.materials[0] : null;
  return stripPrefix(material?.name) || materialRef || "Default material";
};

const resolveMaterialTexturePreviewUrl = (material, texturePreviewUrlByKey) => {
  const colorMap = typeof material?.colorMap === "string" ? material.colorMap : "";
  return colorMap ? texturePreviewUrlByKey[colorMap] || "" : "";
};

const getCollisionGeomIcon = (geom) => {
  if (geom?.mesh) return VisualMeshIcon;
  switch (String(geom?.kind || geom?.type || "").toLowerCase()) {
    case "sphere":
      return SphereGeomIcon;
    case "capsule":
      return CapsuleGeomIcon;
    case "cylinder":
      return CylinderGeomIcon;
    case "ellipsoid":
      return EllipsoidIcon;
    case "box":
    case "rectangle":
    case "plane":
    default:
      return BoxGeomIcon;
  }
};

const getCollisionGeomKindLabel = (geom) =>
  geom?.mesh || String(geom?.kind || geom?.type || "").toLowerCase() === "mesh"
    ? "Mesh"
    : stripPrefix(geom?.kind || geom?.type) || "Collision";

const renderMaterialColorPreview = (color, label, textureUrl = "") => (
  <Tooltip content={label}>
    <span
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: "50%",
        backgroundColor: color || "#ffffff",
        backgroundImage: textureUrl ? `url(${textureUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        border: "1px solid #444",
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  </Tooltip>
);

const buildBodyPanelSnapshot = (bodies, selectedBodyUuid) => {
  if (!selectedBodyUuid) return null;
  const selectedBody = bodies?.[selectedBodyUuid];
  if (!selectedBody) return null;
  const childUuids = Array.isArray(selectedBody.children) ? selectedBody.children : EMPTY_LIST;
  const childBodies = childUuids.map((childUuid) => {
    const childBody = bodies?.[childUuid];
    return {
      uuid: childUuid,
      name: childBody?.name || childUuid,
      joints: Array.isArray(childBody?.joints) ? childBody.joints : EMPTY_LIST,
    };
  });
  return {
    ...selectedBody,
    children: childUuids,
    childBodies,
  };
};

const isSameBodyPanelSnapshot = (prev, next) => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  if (prev.uuid !== next.uuid || prev.name !== next.name) return false;
  if (prev.children !== next.children) return false;
  if (prev.geoms !== next.geoms) return false;
  if (prev.sites !== next.sites) return false;
  if (prev.joints !== next.joints) return false;
  if (prev.inertia !== next.inertia) return false;
  const prevChildren = prev.childBodies || EMPTY_LIST;
  const nextChildren = next.childBodies || EMPTY_LIST;
  if (prevChildren.length !== nextChildren.length) return false;
  for (let index = 0; index < prevChildren.length; index += 1) {
    const prevChild = prevChildren[index];
    const nextChild = nextChildren[index];
    if (
      prevChild?.uuid !== nextChild?.uuid ||
      prevChild?.name !== nextChild?.name ||
      prevChild?.joints !== nextChild?.joints
    ) {
      return false;
    }
  }
  return true;
};

const BodyPropertiesPanel = () => {
  const selectedBodyUuid = useSelectionStore((state) => state.selectedBody);
  const bodyNode = useBodies(
    (allBodies) => buildBodyPanelSnapshot(allBodies, selectedBodyUuid),
    isSameBodyPanelSnapshot
  );
  const bodies = useBodies();
  const equalities = useEqualities();
  const robotActions = useSceneActions();
  const selectionActions = useSelectionStore.getState();
  const sitesMap = useSites();
  const meshesMap = useMeshes();
  const cameras = useCameras();
  const materials = useMaterials(); // Subscribe to material changes
  const { texturePreviewUrlByKey } = useMaterialTextureOptions();
  const contactExcludes = useSceneStore((state) => state.contactExcludes || EMPTY_LIST);
  const isSimRunning = usePhysicsStore((s) => s.isRunning && hasPhysicsWorker());
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();

  // Local state to track current mesh colors for real-time updates
  const [currentMeshColors, setCurrentMeshColors] = useState({});
  const [isAddingContactExclude, setIsAddingContactExclude] = useState(false);
  const [newContactExcludeBodyUuid, setNewContactExcludeBodyUuid] = useState("");
  const childBodies = bodyNode?.childBodies || EMPTY_LIST;
  const hasBodyJoints = Array.isArray(bodyNode?.joints) && bodyNode.joints.length > 0;
  const siteUuids = Array.isArray(bodyNode?.sites) ? bodyNode.sites : EMPTY_LIST;
  const hasSites = siteUuids.length > 0;
  const inertial = bodyNode?.inertia || null;
  const hasInertia = !!inertial;

  const visualMeshGeoms = useMemo(() => {
    if (!Array.isArray(bodyNode?.geoms)) return [];
    return bodyNode.geoms.filter((geom) => geom && geom.mesh && geom.visualOnly === true);
  }, [bodyNode]);

  const visualPrimitiveGeoms = useMemo(() => {
    if (!Array.isArray(bodyNode?.geoms)) return [];
    return bodyNode.geoms.filter((geom) => geom && !geom.mesh && geom.visualOnly === true);
  }, [bodyNode]);
  const visualCount = visualMeshGeoms.length + visualPrimitiveGeoms.length;
  const hasVisualGeoms = visualCount > 0;

  const collisionGeoms = useMemo(() => {
    if (!Array.isArray(bodyNode?.geoms)) return [];
    return bodyNode.geoms.filter((geom) => geom && geom.visualOnly !== true);
  }, [bodyNode]);
  const collisionCount = collisionGeoms.length;
  const hasCollisionGeoms = collisionCount > 0;
  const bodyOptions = useMemo(
    () =>
      Object.values(bodies || {})
        .filter((body) => body?.uuid)
        .map((body) => ({
          value: String(body.uuid),
          label: stripPrefix(body.name) || String(body.uuid),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [bodies]
  );
  const tendons = useTendons();
  const hasRelatedConstraints = useMemo(() => {
    if (!selectedBodyUuid) return false;
    const { relatedEqualities, relatedTendons } = computeRelatedConstraintsLists({
      bodyUuid: selectedBodyUuid,
      bodies,
      tendons,
      equalities,
    });
    return relatedEqualities.length > 0 || relatedTendons.length > 0;
  }, [selectedBodyUuid, bodies, tendons, equalities]);
  const contactExcludesForBody = useMemo(() => {
    if (!selectedBodyUuid) return [];
    return (contactExcludes || [])
      .map((exclude, index) => ({ ...exclude, index }))
      .filter(
        (exclude) =>
          String(exclude.body1) === String(selectedBodyUuid) ||
          String(exclude.body2) === String(selectedBodyUuid)
      );
  }, [contactExcludes, selectedBodyUuid]);
  const contactExcludePartnerOptions = useMemo(() => {
    if (!selectedBodyUuid) return [];
    const excluded = new Set(
      contactExcludesForBody.map((exclude) =>
        String(exclude.body1) === String(selectedBodyUuid)
          ? String(exclude.body2)
          : String(exclude.body1)
      )
    );
    return bodyOptions.filter(
      (option) => option.value !== String(selectedBodyUuid) && !excluded.has(option.value)
    );
  }, [bodyOptions, contactExcludesForBody, selectedBodyUuid]);

  useEffect(() => {
    const ids = new Set(contactExcludePartnerOptions.map((option) => option.value));
    if (newContactExcludeBodyUuid && !ids.has(String(newContactExcludeBodyUuid))) {
      setNewContactExcludeBodyUuid("");
    }
    if (isAddingContactExclude && contactExcludePartnerOptions.length === 0) {
      setIsAddingContactExclude(false);
    }
  }, [contactExcludePartnerOptions, isAddingContactExclude, newContactExcludeBodyUuid]);

  const addContactExclude = useCallback(() => {
    const partnerUuid = newContactExcludeBodyUuid || contactExcludePartnerOptions[0]?.value || "";
    if (!selectedBodyUuid || !partnerUuid || partnerUuid === String(selectedBodyUuid)) return;
    robotActions.addContactExclude({
      body1: selectedBodyUuid,
      body2: partnerUuid,
    });
    setNewContactExcludeBodyUuid("");
    setIsAddingContactExclude(false);
  }, [contactExcludePartnerOptions, newContactExcludeBodyUuid, robotActions, selectedBodyUuid]);

  const updateContactExcludePartner = useCallback(
    (exclude, partnerUuid) => {
      if (!exclude || !partnerUuid || !selectedBodyUuid || partnerUuid === String(selectedBodyUuid))
        return;
      if (String(exclude.body1) === String(selectedBodyUuid)) {
        robotActions.updateContactExclude(exclude.index, { body2: partnerUuid });
      } else {
        robotActions.updateContactExclude(exclude.index, { body1: partnerUuid });
      }
    },
    [robotActions, selectedBodyUuid]
  );

  const canAddCollision = !authoringLocked && !!selectedBodyUuid;
  const handleAddCollision = useCallback(() => {
    if (!canAddCollision) return;
    const addedGeomUuid = addDefaultCollisionGeom(
      robotActions,
      selectedBodyUuid,
      DEFAULT_INSPECTOR_ADD_GEOM_KIND
    );
    if (!addedGeomUuid) return;
    focusNewEntityInInspector(
      "sceneTree",
      () => useSelectionStore.getState().selectGeom(`${selectedBodyUuid}:${addedGeomUuid}`),
      { openPropertiesPanel: false }
    );
  }, [canAddCollision, robotActions, selectedBodyUuid]);

  const canAddVisual = !authoringLocked && !!selectedBodyUuid;
  const handleAddVisual = useCallback(() => {
    if (!canAddVisual) return;
    const addedGeomUuid = addDefaultVisualGeom(
      robotActions,
      selectedBodyUuid,
      DEFAULT_INSPECTOR_ADD_GEOM_KIND
    );
    if (!addedGeomUuid) return;
    focusNewEntityInInspector(
      "sceneTree",
      () => useSelectionStore.getState().selectGeom(`${selectedBodyUuid}:${addedGeomUuid}`),
      { openPropertiesPanel: false }
    );
  }, [canAddVisual, robotActions, selectedBodyUuid]);

  const canAddSite = !authoringLocked && !!selectedBodyUuid;
  const handleAddSite = useCallback(() => {
    if (!canAddSite) return;
    const addedSiteUuid = addDefaultSite(robotActions, selectedBodyUuid);
    if (!addedSiteUuid) return;
    focusNewEntityInInspector(
      "sceneTree",
      () => useSelectionStore.getState().selectSite(addedSiteUuid),
      { openPropertiesPanel: false }
    );
  }, [canAddSite, robotActions, selectedBodyUuid]);

  const canAddInertia = !authoringLocked && !!selectedBodyUuid && !hasInertia;
  const handleAddInertia = useCallback(() => {
    if (!canAddInertia) return;
    const addedInertialUuid = addDefaultInertia(robotActions, selectedBodyUuid);
    if (!addedInertialUuid) return;
    focusNewEntityInInspector(
      "sceneTree",
      () => useSelectionStore.getState().selectInertial(`${selectedBodyUuid}:${addedInertialUuid}`),
      { openPropertiesPanel: false }
    );
  }, [canAddInertia, robotActions, selectedBodyUuid]);

  // Sync current mesh colors with the scene and materials
  useEffect(() => {
    if (!bodyNode || !Array.isArray(bodyNode.geoms)) return;

    const meshGeoms = bodyNode.geoms.filter(
      (g) => g && typeof g.mesh === "string" && g.mesh && g.visualOnly === true
    );
    const colors = {};
    meshGeoms.forEach((geom) => {
      const meshId = geom.mesh;
      if (!meshId) return;

      const matDef = resolveGeomMaterial(geom, materials);
      if (matDef && Array.isArray(matDef.color)) {
        colors[meshId] = materialColorToCss(matDef);
      } else {
        const actualColor = robotActions.getMeshColor(meshId);
        colors[meshId] = actualColor || "#ffffff";
      }
    });

    setCurrentMeshColors(colors);
  }, [bodyNode, materials, robotActions]);

  if (!bodyNode) {
    return null;
  }

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <BodyVisibilityToolbar bodyUuid={selectedBodyUuid} showBodyLabelMarkerToggle />
      </div>
    );
  }

  const renderInspectableItem = (
    label,
    onInspect,
    rightContent = null,
    itemKey,
    inspectLabel,
    deleteConfig = null
  ) => (
    <InspectableMenuRow
      key={String(itemKey)}
      onInspect={onInspect}
      rightContent={rightContent}
      inspectLabel={inspectLabel}
      renameValue={deleteConfig?.renameValue}
      onRename={deleteConfig?.onRename || null}
      renameLabel={deleteConfig?.renameLabel}
      onDelete={deleteConfig?.onDelete || null}
      deleteLabel={deleteConfig?.label}
      rightContentClassName={deleteConfig?.rightContentClassName}
    >
      {label}
    </InspectableMenuRow>
  );
  const showContactExclusions = !!selectedBodyUuid;
  const hasChildJointSection = childBodies.length > 0;
  const separatorAfterBodyJoints =
    hasChildJointSection ||
    hasVisualGeoms ||
    hasCollisionGeoms ||
    hasSites ||
    hasRelatedConstraints ||
    hasInertia ||
    showContactExclusions;
  const separatorAfterChildJoints =
    hasVisualGeoms ||
    hasCollisionGeoms ||
    hasSites ||
    hasRelatedConstraints ||
    hasInertia ||
    showContactExclusions;
  const separatorAfterVisuals =
    hasCollisionGeoms || hasSites || hasRelatedConstraints || hasInertia || showContactExclusions;
  const separatorAfterCollisions =
    hasSites || hasRelatedConstraints || hasInertia || showContactExclusions;
  const separatorAfterSites = hasRelatedConstraints || hasInertia || showContactExclusions;
  const separatorAfterRelatedConstraints = hasInertia || showContactExclusions;
  const separatorAfterInertials = showContactExclusions;

  return (
    <div className="p-3 space-y-4">
      <BodyVisibilityToolbar bodyUuid={selectedBodyUuid} showBodyLabelMarkerToggle />

      <Separator />

      <BodyTransformSection
        selectedBodyUuid={selectedBodyUuid}
        robotActions={robotActions}
        isSimRunning={isSimRunning}
        physicsAuthoringLocked={authoringLocked}
        transformEditorBlock={transformEditorBlock}
        showPositionGizmo={showPositionGizmo}
        showRotationGizmo={showRotationGizmo}
        onTogglePositionGizmo={togglePositionGizmo}
        onToggleRotationGizmo={toggleRotationGizmo}
      />

      <Separator />

      <BodyQuickAddSection
        bodyUuid={selectedBodyUuid}
        bodyName={bodyNode?.name}
        bodies={bodies}
        cameras={cameras}
        equalities={equalities}
        hasInertia={hasInertia}
        disabled={authoringLocked}
        sceneActions={robotActions}
      />

      <Separator />

      {/* Joints (upstream) */}
      {hasBodyJoints ? (
        <>
          <BodyJointsSection
            jointUuids={bodyNode.joints}
            isSimRunning={isSimRunning}
            robotActions={robotActions}
            selectionActions={selectionActions}
          />
          {separatorAfterBodyJoints ? <Separator /> : null}
        </>
      ) : null}

      {/* Joints (children) */}
      {hasChildJointSection ? (
        <>
          <ChildBodyJointsSection
            childBodies={childBodies}
            isSimRunning={isSimRunning}
            physicsAuthoringLocked={authoringLocked}
            robotActions={robotActions}
            selectionActions={selectionActions}
          />
          {separatorAfterChildJoints ? <Separator /> : null}
        </>
      ) : null}

      {/* Visuals */}
      {hasVisualGeoms ? (
        <>
          <Collapsible defaultOpen>
            <CollapsibleTrigger
              quiet
              aria-label="Toggle Visuals"
              actions={
                <Tooltip content={authoringLocked ? "Stop physics to add a visual" : "Add visual"}>
                  <button
                    type="button"
                    disabled={!canAddVisual}
                    aria-label="Add visual"
                    onClick={handleAddVisual}
                    className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                    style={{
                      opacity: !canAddVisual ? 0.35 : 0.55,
                    }}
                  >
                    <Plus size={16} aria-hidden="true" />
                  </button>
                </Tooltip>
              }
            >
              <span className="min-w-0 truncate text-left">Visuals ({visualCount})</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-1">
                <>
                  {visualMeshGeoms.map((g, index) => {
                    const meshId = g.mesh;
                    const meshDef = meshesMap?.[meshId] || null;
                    const material = resolveGeomMaterial(g, materials);
                    const textureUrl = resolveMaterialTexturePreviewUrl(
                      material,
                      texturePreviewUrlByKey
                    );
                    const displayName = stripPrefix(
                      g.name || (meshDef && meshDef.name ? meshDef.name : meshId)
                    );
                    return renderInspectableItem(
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {renderMaterialColorPreview(
                          currentMeshColors[meshId] || "#ffffff",
                          getMaterialPreviewLabel(g, material),
                          textureUrl
                        )}
                        <span>{displayName}</span>
                      </span>,
                      () =>
                        g.uuid
                          ? selectionActions.selectGeom(`${selectedBodyUuid}:${g.uuid}`)
                          : selectionActions.selectMesh(meshId),
                      null,
                      `${selectedBodyUuid}_${g.uuid || meshId || index}_visual_mesh`,
                      `Inspect ${displayName}`,
                      !authoringLocked && g.uuid
                        ? {
                            label: `Delete ${displayName}`,
                            renameLabel: `Rename ${displayName}`,
                            renameValue: displayName,
                            onRename: (nextName) =>
                              robotActions.setGeomName(selectedBodyUuid, String(g.uuid), nextName),
                            onDelete: () =>
                              robotActions.removeGeom(selectedBodyUuid, String(g.uuid)),
                          }
                        : null
                    );
                  })}
                  {visualPrimitiveGeoms.map((geom, index) => {
                    const displayName =
                      stripPrefix(geom.name || geom.kind || geom.type) || "Visual";
                    const material = resolveGeomMaterial(geom, materials);
                    const materialColor = materialColorToCss(material);
                    const textureUrl = resolveMaterialTexturePreviewUrl(
                      material,
                      texturePreviewUrlByKey
                    );
                    return renderInspectableItem(
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {renderMaterialColorPreview(
                          materialColor,
                          getMaterialPreviewLabel(geom, material),
                          textureUrl
                        )}
                        <span>{displayName}</span>
                      </span>,
                      () =>
                        geom.uuid
                          ? selectionActions.selectGeom(`${selectedBodyUuid}:${geom.uuid}`)
                          : null,
                      <span className="text-xs text-gray-400 select-none">
                        {geom.kind || geom.type || "primitive"}
                      </span>,
                      `${selectedBodyUuid}_${geom.uuid || index}_primitive`,
                      `Inspect ${displayName}`,
                      !authoringLocked && geom.uuid
                        ? {
                            label: `Delete ${displayName}`,
                            renameLabel: `Rename ${displayName}`,
                            renameValue: displayName,
                            onRename: (nextName) =>
                              robotActions.setGeomName(
                                selectedBodyUuid,
                                String(geom.uuid),
                                nextName
                              ),
                            onDelete: () =>
                              robotActions.removeGeom(selectedBodyUuid, String(geom.uuid)),
                          }
                        : null
                    );
                  })}
                </>
              </div>
            </CollapsibleContent>
          </Collapsible>
          {separatorAfterVisuals ? <Separator /> : null}
        </>
      ) : null}

      {/* Collisions */}
      {hasCollisionGeoms ? (
        <>
          <Collapsible defaultOpen={false}>
            <CollapsibleTrigger
              quiet
              aria-label="Toggle Collisions"
              actions={
                <Tooltip
                  content={authoringLocked ? "Stop physics to add a collision" : "Add collision"}
                >
                  <button
                    type="button"
                    disabled={!canAddCollision}
                    aria-label="Add collision"
                    onClick={handleAddCollision}
                    className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                    style={{
                      opacity: !canAddCollision ? 0.35 : 0.55,
                    }}
                  >
                    <Plus size={16} aria-hidden="true" />
                  </button>
                </Tooltip>
              }
            >
              <span className="min-w-0 truncate text-left">Collisions ({collisionCount})</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-1">
                {collisionGeoms.map((geom, index) => {
                  const meshId = geom.mesh || null;
                  const meshDef = meshId ? meshesMap?.[meshId] || null : null;
                  const CollisionIcon = getCollisionGeomIcon(geom);
                  const displayName =
                    stripPrefix(
                      geom.name ||
                        (meshDef && meshDef.name ? meshDef.name : meshId) ||
                        geom.kind ||
                        geom.type
                    ) || "Collision";
                  return renderInspectableItem(
                    <span className="flex min-w-0 items-center gap-2">
                      <Tooltip content={getCollisionGeomKindLabel(geom)}>
                        <CollisionIcon
                          className="h-3.5 w-3.5 shrink-0 text-gray-400"
                          aria-hidden="true"
                        />
                      </Tooltip>
                      <span className="min-w-0 truncate">{displayName}</span>
                    </span>,
                    () =>
                      geom.uuid
                        ? selectionActions.selectGeom(`${selectedBodyUuid}:${geom.uuid}`)
                        : null,
                    null,
                    `${selectedBodyUuid}_${geom.uuid || meshId || index}_collision`,
                    `Inspect ${displayName}`,
                    !authoringLocked && geom.uuid
                      ? {
                          label: `Delete ${displayName}`,
                          renameLabel: `Rename ${displayName}`,
                          renameValue: displayName,
                          onRename: (nextName) =>
                            robotActions.setGeomName(selectedBodyUuid, String(geom.uuid), nextName),
                          onDelete: () =>
                            robotActions.removeGeom(selectedBodyUuid, String(geom.uuid)),
                        }
                      : null
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
          {separatorAfterCollisions ? <Separator /> : null}
        </>
      ) : null}

      {/* Sites */}
      {hasSites ? (
        <>
          <Collapsible defaultOpen>
            <CollapsibleTrigger
              quiet
              aria-label="Toggle Sites"
              actions={
                <Tooltip content={authoringLocked ? "Stop physics to add a site" : "Add site"}>
                  <button
                    type="button"
                    disabled={!canAddSite}
                    aria-label="Add site"
                    onClick={handleAddSite}
                    className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                    style={{
                      opacity: !canAddSite ? 0.35 : 0.55,
                    }}
                  >
                    <Plus size={16} aria-hidden="true" />
                  </button>
                </Tooltip>
              }
            >
              <span className="min-w-0 truncate text-left">Sites ({siteUuids.length})</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-1">
                {siteUuids.map((siteUuid, index) => {
                  const siteData = sitesMap?.[siteUuid];
                  const siteName = stripPrefix(siteData?.name) || siteUuid || `site-${index}`;
                  return renderInspectableItem(
                    siteName,
                    () => selectionActions.selectSite(siteUuid),
                    null,
                    siteUuid,
                    `Inspect ${siteName}`,
                    !authoringLocked
                      ? {
                          label: `Delete ${siteName}`,
                          renameLabel: `Rename ${siteName}`,
                          renameValue: siteName,
                          onRename: (nextName) => robotActions.setSiteName(siteUuid, nextName),
                          onDelete: () => robotActions.removeSite(siteUuid),
                        }
                      : null
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
          {separatorAfterSites ? <Separator /> : null}
        </>
      ) : null}

      {/* Related Constraints */}
      {hasRelatedConstraints ? (
        <>
          <RelatedConstraintsSection bodyUuid={selectedBodyUuid} />
          {separatorAfterRelatedConstraints ? <Separator /> : null}
        </>
      ) : null}

      {/* Inertials */}
      {hasInertia ? (
        <>
          <Collapsible defaultOpen={false}>
            <CollapsibleTrigger
              quiet
              aria-label="Toggle Inertials"
              actions={
                <Tooltip
                  content={
                    authoringLocked
                      ? "Stop physics to add an inertial"
                      : "This body already has an inertial"
                  }
                >
                  <button
                    type="button"
                    disabled={!canAddInertia}
                    aria-label="Add inertial"
                    onClick={handleAddInertia}
                    className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                    style={{
                      opacity: !canAddInertia ? 0.35 : 0.55,
                    }}
                  >
                    <Plus size={16} aria-hidden="true" />
                  </button>
                </Tooltip>
              }
            >
              <span className="min-w-0 truncate text-left">Inertials (1)</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-1">
                {renderInspectableItem(
                  stripPrefix(inertial.name) || "Inertial",
                  () =>
                    inertial.uuid
                      ? selectionActions.selectInertial(`${selectedBodyUuid}:${inertial.uuid}`)
                      : null,
                  <span className="text-xs text-gray-400 select-none">
                    {Number.isFinite(Number(inertial.mass))
                      ? Number(inertial.mass).toFixed(3)
                      : "1.000"}{" "}
                    kg
                  </span>,
                  `${selectedBodyUuid}_${inertial.uuid || "inertial"}`,
                  `Inspect ${stripPrefix(inertial.name) || "Inertial"}`,
                  !authoringLocked
                    ? {
                        label: `Delete ${stripPrefix(inertial.name) || "Inertial"}`,
                        renameLabel: `Rename ${stripPrefix(inertial.name) || "Inertial"}`,
                        renameValue: stripPrefix(inertial.name) || "Inertial",
                        rightContentClassName: "inspectable-menu-row__optional-right",
                        onRename: (nextName) =>
                          robotActions.setInertiaName(selectedBodyUuid, nextName),
                        onDelete: () => robotActions.removeInertia(selectedBodyUuid),
                      }
                    : null
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
          {separatorAfterInertials ? <Separator /> : null}
        </>
      ) : null}

      {/* Contact Exclusions */}
      {showContactExclusions ? (
        <BodyContactExclusionsSection
          authoringLocked={authoringLocked}
          bodyOptions={bodyOptions}
          contactExcludePartnerOptions={contactExcludePartnerOptions}
          contactExcludesForBody={contactExcludesForBody}
          isAddingContactExclude={isAddingContactExclude}
          newContactExcludeBodyUuid={newContactExcludeBodyUuid}
          onAddContactExclude={addContactExclude}
          onCancelAddContactExclude={() => {
            setIsAddingContactExclude(false);
            setNewContactExcludeBodyUuid("");
          }}
          onNewContactExcludeBodyUuidChange={setNewContactExcludeBodyUuid}
          onRemoveContactExclude={(index) => robotActions.removeContactExclude(index)}
          onRequestAddContactExclude={() => setIsAddingContactExclude(true)}
          onUpdateContactExcludePartner={updateContactExcludePartner}
          selectedBodyUuid={selectedBodyUuid}
        />
      ) : null}
    </div>
  );
};

export default React.memo(BodyPropertiesPanel);
